/* components/kitchen/PriceForm.jsx — фиксация закупочной цены + история цен.
   Переиспользуется в IngredientEditor (вкладка «Закупки») и потенциально вне его.
   props: prices[], onAdd(price), onDelete(id). Удаление — через ConfirmDialog (фикс №13).
   ХЕНДОФФ → frontend/src/components/kitchen/PriceForm.jsx. */

import React from 'react';
import { useConfirm } from '../../app/providers/ConfirmProvider.jsx';
import { Button } from '../ui/Button.jsx';
import { IconButton } from '../ui/Buttons.jsx';
import { Field, NativeSelect, NumberInput, TextInput, Textarea } from '../ui/Field.jsx';
import { Icon } from '../ui/Icon.jsx';
import { hooks } from '../../hooks/index.jsx';
import { fmt } from '../../lib/format.js';

  const { useState } = React;
  const { useBreakpoint } = hooks;

  // Только единицы с боевым slug (kg/l/pcs/g) — IngredientPrice.UNIT_CHOICES. «пуч./дес./уп.» сняты
  // (бэк 400-нёт «Неизвестная единица»). См. lib/adapters/ingredients.PRICE_UNIT_RU_TO_SLUG.
  const UNITS = ['кг', 'л', 'шт', 'г'];
  const today = () => new Date().toISOString().slice(0, 10);

  function PriceForm({ prices = [], onAdd, onDelete, pieceWeightG = null }) {
    const confirm = useConfirm();
    const { isMobile } = useBreakpoint();
    const [open, setOpen] = useState(false);
    const [amount, setAmount] = useState('');
    const [unit, setUnit] = useState('кг');
    const [date, setDate] = useState(today());
    const [supplier, setSupplier] = useState('');
    const [notes, setNotes] = useState('');
    const [pieceW, setPieceW] = useState('');

    // Цена «₽/шт» без веса штуки не конвертируется в ₽/г — себестоимость блюд
    // с этим сырьём молча не считается. Если у карточки веса ещё нет, форма
    // требует его вместе с ценой (уйдёт PATCH'ем карточки перед POST цены).
    const needPieceW = unit === 'шт' && !pieceWeightG;
    const pieceMissing = needPieceW && (pieceW === '' || Number(pieceW) <= 0);

    const reset = () => { setAmount(''); setUnit('кг'); setDate(today()); setSupplier(''); setNotes(''); setPieceW(''); };
    const submit = () => {
      if (amount === '' || Number(amount) <= 0 || pieceMissing) return;
      onAdd && onAdd({
        id: 'p' + Date.now(), amount_rub: Number(amount), unit,
        effective_from: date, supplier_name: supplier.trim() || null, notes: notes.trim(),
        ...(needPieceW ? { piece_weight_g: Number(pieceW) } : {}),
      });
      reset(); setOpen(false);
    };
    const del = async (id) => {
      const ok = await confirm({ title: 'Удалить запись цены?', body: 'Запись истории закупочных цен будет удалена. Действие нельзя отменить.', confirmLabel: 'Удалить', tone: 'danger' });
      if (ok) onDelete && onDelete(id);
    };

    return (
      <div className="price-block">
        <div className="price-block__head">
          <h4 className="ttk-sub-title t-caption">История закупочных цен</h4>
          {!open && <IconButton icon="plus" size="sm" variant="secondary" tooltip="Обновить цену" onClick={() => setOpen(true)} />}
        </div>

        {open && (
          <div className="price-form-full">
            <div className="price-form-full__grid">
              <Field label="Сумма" required><NumberInput value={amount} unit="₽" step={10} min={0} onChange={setAmount} /></Field>
              <Field label="За единицу"><NativeSelect value={unit} options={UNITS} onChange={(e) => setUnit(e.target.value)} /></Field>
              <Field label="Действует с"><input type="date" className="input price-form-full__date" value={date} onChange={(e) => setDate(e.target.value)} /></Field>
              <Field label="Поставщик"><TextInput value={supplier} placeholder="ООО «…»" onChange={(e) => setSupplier(e.target.value)} /></Field>
              {needPieceW && (
                <Field label="Вес 1 шт" required hint="Нужен, чтобы пересчитать ₽/шт в себестоимость блюд">
                  <NumberInput value={pieceW} unit="г" step={5} min={0} onChange={setPieceW} />
                </Field>
              )}
            </div>
            <Field label="Заметки"><Textarea value={notes} minRows={1} placeholder="Условия поставки, акция, минимальный объём…" onChange={(e) => setNotes(e.target.value)} /></Field>
            <div className="price-form-full__act">
              <Button variant="ghost" size="sm" onClick={() => { reset(); setOpen(false); }}>Отмена</Button>
              <Button variant="primary" size="sm" onClick={submit} disabled={amount === '' || pieceMissing}>Добавить</Button>
            </div>
          </div>
        )}

        {prices.length === 0 && !open ? (
          <p className="t-body-sm text-mute price-block__empty">Цена не задана — себестоимость блюд с этим сырьём будет неполной.</p>
        ) : prices.length > 0 ? (
          isMobile ? (
            <ul className="price-cards">
              {prices.map((p, i) => (
                <li key={p.id} className={'price-card' + (i === 0 ? ' is-current' : '')}>
                  <div className="price-card__top">
                    <span className="price-card__amount num">{fmt.money(p.amount_rub)}/{p.unit}</span>
                    <span className="grow" />
                    <IconButton icon="trash" size="sm" variant="ghost" tooltip="Удалить" onClick={() => del(p.id)} />
                  </div>
                  <dl className="price-card__meta">
                    <div><dt>Дата</dt><dd className="num">{fmt.date(p.effective_from)}</dd></div>
                    <div><dt>Поставщик</dt><dd>{p.supplier_name || '—'}</dd></div>
                    {p.notes && <div className="price-card__notes"><dt>Заметки</dt><dd>{p.notes}</dd></div>}
                  </dl>
                </li>
              ))}
            </ul>
          ) : (
          <table className="price-table">
            <thead>
              <tr><th>Дата</th><th>Поставщик</th><th className="num">Сумма</th><th>Заметки</th><th /></tr>
            </thead>
            <tbody>
              {prices.map((p, i) => (
                <tr key={p.id}>
                  <td className="num">{fmt.date(p.effective_from)}{i === 0 && <span className="badge badge--accent badge--sm price-table__cur">текущая</span>}</td>
                  <td className="truncate">{p.supplier_name || '—'}</td>
                  <td className="num price-table__amount">{fmt.money(p.amount_rub)}/{p.unit}</td>
                  <td className="truncate text-dim">{p.notes || '—'}</td>
                  <td className="price-table__act"><IconButton icon="trash" size="sm" variant="ghost" tooltip="Удалить" onClick={() => del(p.id)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          )
        ) : null}
      </div>
    );
  }

export { PriceForm };
